import type { ClassSummary, StaffSummary } from '@school-erp/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, errorMessage, primaryButton, selectClass } from '../../components/common';
import { t } from '../../i18n';

interface ClassTeacher {
  sectionId: string;
  staffId: string;
  staffName: string;
}
interface Assignment {
  id: string;
  className: string;
  sectionName: string;
  sectionId: string;
  subjectName: string;
  subjectId: string;
  staffName: string;
}
interface Subject {
  id: string;
  name: string;
}

export function TeachingTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState({ sectionId: '', subjectId: '', staffId: '' });

  const classes = useQuery({
    queryKey: ['classes', schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${schoolId}/classes`),
  });
  const subjects = useQuery({
    queryKey: ['subjects', schoolId],
    queryFn: () => api<Subject[]>(`/schools/${schoolId}/subjects`),
  });
  const teachers = useQuery({
    queryKey: ['staff', 'teaching', schoolId],
    queryFn: () =>
      apiList<StaffSummary>('/staff', {
        query: { schoolId, staffType: 'teaching', status: 'active', pageSize: 100 },
      }),
    enabled: can('staff.view', schoolId),
  });
  const classTeachers = useQuery({
    queryKey: ['class-teachers', schoolId],
    queryFn: () => api<ClassTeacher[]>(`/schools/${schoolId}/class-teachers`),
  });
  const assignments = useQuery({
    queryKey: ['assignments', schoolId],
    queryFn: () => api<Assignment[]>(`/schools/${schoolId}/teaching-assignments`),
  });

  const refresh = () =>
    Promise.all(
      ['class-teachers', 'assignments'].map((k) =>
        queryClient.invalidateQueries({ queryKey: [k, schoolId] }),
      ),
    );
  const fail = (e: unknown) => setMessage(errorMessage(e));
  const manage = can('assignments.manage', schoolId);

  const setClassTeacher = useMutation({
    mutationFn: ({ sectionId, staffId }: { sectionId: string; staffId: string }) =>
      staffId
        ? api(`/schools/${schoolId}/sections/${sectionId}/class-teacher`, {
            method: 'PUT',
            body: { staffId },
          })
        : api(`/schools/${schoolId}/sections/${sectionId}/class-teacher`, { method: 'DELETE' }),
    onSuccess: () => {
      setMessage(null);
      void refresh();
    },
    onError: fail,
  });
  const assign = useMutation({
    mutationFn: () =>
      api(`/schools/${schoolId}/teaching-assignments`, { method: 'POST', body: form }),
    onSuccess: () => {
      setMessage(null);
      void refresh();
    },
    onError: fail,
  });
  const unassign = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/teaching-assignments/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: fail,
  });

  const sections = (classes.data ?? []).flatMap((c) =>
    c.sections.map((s) => ({ id: s.id, label: `${c.name} – ${s.name}` })),
  );
  const teacherOptions = teachers.data?.data ?? [];

  return (
    <section aria-label={t('acad.tab.teaching')} className="space-y-6">
      {message && <Notice kind="error">{message}</Notice>}

      <div className="space-y-2">
        <h3 className="text-base font-semibold">{t('acad.classTeachers')}</h3>
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {sections.map((sec) => {
            const current = classTeachers.data?.find((ct) => ct.sectionId === sec.id);
            return (
              <li key={sec.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
                <span className="font-medium">{sec.label}</span>
                {manage ? (
                  <label>
                    <span className="sr-only">
                      {t('acad.teacher')} — {sec.label}
                    </span>
                    <select
                      value={current?.staffId ?? ''}
                      onChange={(e) =>
                        setClassTeacher.mutate({ sectionId: sec.id, staffId: e.target.value })
                      }
                      className={`${selectClass} mt-0`}
                    >
                      <option value="">{t('acad.noTeacher')}</option>
                      {teacherOptions.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.firstName} {s.lastName}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <span>{current?.staffName ?? t('acad.noTeacher')}</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="space-y-2">
        <h3 className="text-base font-semibold">{t('acad.subjectTeachers')}</h3>
        {manage && (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (form.sectionId && form.subjectId && form.staffId) assign.mutate();
            }}
            className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          >
            {(
              [
                ['sectionId', 'acad.section', sections.map((s) => [s.id, s.label])],
                ['subjectId', 'acad.subject', (subjects.data ?? []).map((s) => [s.id, s.name])],
                [
                  'staffId',
                  'acad.teacher',
                  teacherOptions.map((s) => [s.id, `${s.firstName} ${s.lastName}`]),
                ],
              ] as const
            ).map(([key, label, options]) => (
              <div key={key}>
                <label htmlFor={`assign-${key}`} className="block text-sm font-medium">
                  {t(label)}
                </label>
                <select
                  id={`assign-${key}`}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className={selectClass}
                >
                  <option value="" />
                  {options.map(([id, text]) => (
                    <option key={id} value={id}>
                      {text}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            <div className="self-end">
              <button type="submit" disabled={assign.isPending} className={primaryButton}>
                {t('acad.assign')}
              </button>
            </div>
          </form>
        )}
        {assignments.data?.length === 0 && (
          <p className="text-slate-600">{t('acad.noAssignments')}</p>
        )}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {assignments.data?.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-2 p-3">
              <span>
                {a.className} – {a.sectionName} ·{' '}
                <span className="font-medium">{a.subjectName}</span> · {a.staffName}
              </span>
              {manage && (
                <button
                  type="button"
                  className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                  onClick={() => unassign.mutate(a.id)}
                >
                  {t('acad.delete')}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
